import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  alpha,
  useTheme,
  type Theme,
} from "@mui/material";
import { metricLabel } from "../../utils/metricOptions";
import { useReportStats } from "../../hooks/useReportData";
import type { ExecSummaryBlock } from "../../api/types";

interface ExecSummaryViewProps {
  block: ExecSummaryBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  fill?: boolean;
}

/** Maps an API RAG colour to a soft MUI palette background for a table cell. */
const ragBg = (theme: Theme, ragColor?: string): string | undefined => {
  switch (ragColor) {
    case "green":
      return alpha(theme.palette.success.main, 0.18);
    case "amber":
      return alpha(theme.palette.warning.main, 0.18);
    case "red":
      return alpha(theme.palette.error.main, 0.18);
    default:
      return undefined;
  }
};

const fmt = (value: number | null | undefined, unit: string | null): string => {
  if (value === null || value === undefined) return "–";
  const rounded = Math.round(value * 10) / 10;
  return unit ? `${rounded}${unit}` : `${rounded}`;
};

/**
 * Executive summary RAG table: one column per metric, rows for last period /
 * this period / change. The change cell is tinted by the metric's RAG colour.
 */
export const ExecSummaryView: React.FC<ExecSummaryViewProps> = ({
  block,
  clientId,
  branding,
}) => {
  const theme = useTheme();
  const accent = branding?.primaryColor ?? theme.palette.primary.main;
  const query = useReportStats(block.metrics, block.filters, clientId);

  if (query.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (query.isError) {
    return <Alert severity="error">Couldn't load executive summary data.</Alert>;
  }

  const stats = query.data?.data ?? {};
  const columns = block.metrics.filter((m) => stats[m]);
  if (columns.length === 0) {
    return <Alert severity="info">No summary data for this period.</Alert>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      {block.title && (
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {block.title}
        </Typography>
      )}

      <Table size="small" component={Paper} sx={{ overflow: "hidden" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }} />
            {columns.map((m) => (
              <TableCell key={m} align="center" sx={{ fontWeight: 700 }}>
                {stats[m]?.label ?? metricLabel(m)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Last period</TableCell>
            {columns.map((m) => (
              <TableCell key={m} align="center">
                {fmt(stats[m]?.previous_period_value, stats[m]?.unit ?? null)}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>This period</TableCell>
            {columns.map((m) => (
              <TableCell key={m} align="center" sx={{ fontWeight: 700 }}>
                {fmt(stats[m]?.value, stats[m]?.unit ?? null)}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Change</TableCell>
            {columns.map((m) => (
              <TableCell
                key={m}
                align="center"
                sx={{ bgcolor: ragBg(theme, stats[m]?.rag_color), fontSize: 13 }}
              >
                {stats[m]?.change ?? "–"}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>

      {block.keyMessage && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 1,
            borderLeft: `4px solid ${accent}`,
            bgcolor: alpha(accent, 0.08),
          }}
        >
          <Typography
            variant="overline"
            sx={{ color: accent, fontWeight: 700, letterSpacing: "0.06em" }}
          >
            Key Message
          </Typography>
          <Typography variant="body1">{block.keyMessage}</Typography>
        </Box>
      )}
    </Box>
  );
};
